<template>
  <div>
    <div
      v-if="!occurrences.length"
      class="text-center py-4"
    >
      <feather-icon
        v-if="emptyIcon"
        :icon="emptyIcon"
        size="40"
        class="text-muted mb-2"
      />
      <p class="text-muted mb-0">
        {{ emptyText }}
      </p>
    </div>

    <div
      v-else
      class="occ-table-wrapper"
    >
      <table class="table table-hover mb-0 occ-table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="[
                col.sticky ? `sticky-${col.sticky}` : null,
                col.align === 'center' ? 'text-center' : null,
                col.sortable === false ? null : 'sortable',
              ]"
              :style="col.width ? { width: col.width } : null"
              @click="col.sortable === false ? null : toggleSort(col.key)"
            >
              {{ col.labelKey ? $t(col.labelKey) : col.label }}
              <feather-icon
                v-if="sortKey === col.key"
                :icon="sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="12"
              />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="occ in occurrences"
            :key="occ.id"
            :class="{ 'occ-row--mine': highlightMine && isMine(occ) }"
          >
            <td class="sticky-left">
              <span
                class="badge text-monospace"
                :class="{ 'protocol-link': podeAbrir }"
                :style="protocolBadgeStyle(occ)"
                :role="podeAbrir ? 'button' : null"
                :tabindex="podeAbrir ? 0 : null"
                :title="podeAbrir ? 'Abrir ocorrência' : null"
                @click="abrir(occ)"
                @keydown.enter.prevent="abrir(occ)"
                @keydown.space.prevent="abrir(occ)"
              >{{ occ.protocol }}</span>
              <div
                v-if="isFromChildNode(occ)"
                class="occ-node-hint"
                :title="occ.node && occ.node.name"
              >
                <feather-icon icon="GitBranchIcon" size="10" />
                {{ occ.node && occ.node.name }}
              </div>
            </td>
            <td>{{ occ.requester_name }}</td>
            <td>{{ $occurrencePhone(occ) || "-" }}</td>
            <td
              class="cell-truncate"
              :title="fullAddress(occ)"
            >
              <span v-if="occ.address">
                {{ occ.address }}{{ occ.number ? ", " + occ.number : "" }}
                <span
                  v-if="occ.neighborhood"
                  class="text-muted"
                >&mdash; {{ occ.neighborhood }}</span>
              </span>
              <span
                v-else
                class="text-muted"
              >-</span>
            </td>
            <td>{{ occ.discipline ? occ.discipline.name : "-" }}</td>
            <td>{{ occ.dispatch_type ? occ.dispatch_type.name : "-" }}</td>
            <td>{{ occ.dispatch_reason ? occ.dispatch_reason.name : "-" }}</td>
            <td>
              <span
                v-if="highlightMine && isMine(occ)"
                class="occ-mine-label"
              >Você</span>
              <template v-else>
                {{ responsibleName(occ) }}
              </template>
            </td>
            <td>
              <span
                class="badge"
                :style="stepBadgeStyle(occ.current_step, occ)"
              >
                {{ stepLabel(occ.current_step, occ) }}
              </span>
              <!-- Tempo na etapa, logo abaixo dela; o que passou do perigo vai na coluna ao lado. -->
              <div class="mt-25">
                <span
                  :class="elapsedBadgeClass(
                    occ.current_step_since || occ.created_at,
                    occ.current_step,
                    occ.discipline_id,
                  )"
                >
                  {{ elapsedHHMMSS(occ.current_step_since || occ.created_at) }}
                </span>
              </div>
            </td>
            <td class="text-nowrap">
              {{ formatDate(occ.created_at) }}
            </td>
            <td>
              <span
                v-if="responseTimeOf(occ).text"
                class="badge response-badge"
                :class="{ piscar: responseTimeOf(occ).running }"
                :style="responseBadgeStyle(occ)"
                :title="$t('occurrenceTable.responseTimeHint')"
              >{{ responseTimeOf(occ).text }}</span>
              <span
                v-else
                class="text-muted"
              >—</span>
            </td>
            <td class="text-center sticky-right">
              <div class="occ-actions">
                <button
                  v-if="podeAbrir"
                  class="btn btn-sm btn-outline-primary"
                  :title="$t('occurrenceTable.actions.attend')"
                  @click="abrir(occ)"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="13"
                  />
                </button>
                <button
                  v-if="podeAbrir"
                  class="btn btn-sm btn-outline-secondary"
                  :title="$t('occurrenceTable.actions.viewMap')"
                  @click="verNoMapa(occ)"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="13"
                  />
                </button>
                <button
                  v-if="podeFinalizar"
                  class="btn btn-sm btn-outline-danger"
                  :title="$t('occurrenceTable.actions.finalize')"
                  @click="encerrar(occ)"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="13"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <OccurrenceMapModal
      :visible.sync="showMapModal"
      :occurrence-id="mapOccurrenceId"
    />

    <FinalizeOccurrenceModal
      :visible.sync="showFinalizeModal"
      :occurrence-id="finalizeOccurrence ? finalizeOccurrence.id : null"
      :discipline-id="finalizeOccurrence ? finalizeOccurrence.discipline_id : null"
      @finalized="onFinalized"
      @open-pendency="onOpenPendency"
    />
  </div>
</template>

<script>
import ElapsedTimeMixin from '@/mixins/ElapsedTimeMixin'
import { stepLabel as occurrenceStepLabel } from '@/utils/occurrenceStep'
import OccurrenceMapModal from '@/components/OccurrenceMapModal.vue'
import FinalizeOccurrenceModal from '@/components/FinalizeOccurrenceModal.vue'
import { getContrastTextColor } from '@/utils/color'

export default {
  name: 'OccurrenceTable',

  components: { OccurrenceMapModal, FinalizeOccurrenceModal },

  mixins: [ElapsedTimeMixin],

  props: {
    highlightMine: {
      type: Boolean,
      default: false,
    },
    occurrences: {
      type: Array,
      required: true,
    },
    emptyText: {
      type: String,
      default: 'Nenhuma ocorrência.',
    },
    emptyIcon: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      sortKey: null,
      sortDir: 'asc',
      columns: [
        { key: 'protocol', label: 'Protocolo', sticky: 'left' },
        { key: 'requester_name', label: 'Solicitante' },
        { key: 'requester_phone', label: 'Telefone' },
        { key: 'address', label: 'Endereço' },
        { key: 'discipline', label: 'Disciplina' },
        { key: 'dispatch_type', label: 'Tipo' },
        { key: 'dispatch_reason', label: 'Motivo' },
        { key: 'responsible', label: 'Responsável' },
        { key: 'current_step', label: 'Etapa' },
        { key: 'created_at', label: 'Aberta em' },
        { key: 'elapsed', labelKey: 'occurrenceTable.responseTime', sortable: false },
        {
          key: 'actions', label: 'Ações', sortable: false, sticky: 'right', align: 'center', width: '220px',
        },
      ],
      showMapModal: false,
      mapOccurrenceId: null,
      showFinalizeModal: false,
      finalizeOccurrence: null,
    }
  },

  computed: {
    podeAbrir() {
      return this.$hasAbility('regulacao.visualizar')
    },
    podeFinalizar() {
      return this.$hasAbility('ocorrencia.finalizar')
    },
  },

  methods: {
    abrir(occ) {
      if (!this.podeAbrir) return

      this.$emit('open', occ)
    },

    verNoMapa(occ) {
      this.mapOccurrenceId = occ.id
      this.showMapModal = true
    },

    encerrar(occ) {
      this.finalizeOccurrence = occ
      this.showFinalizeModal = true
    },

    onFinalized() {
      this.showFinalizeModal = false
      this.$emit('refresh')
    },

    onOpenPendency() {
      const occ = this.finalizeOccurrence
      this.showFinalizeModal = false
      if (occ) this.abrir(occ)
    },

    // ── Ordenação ───────────────────────────────────────────────────────
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }

      this.emitChange()
    },

    emitChange() {
      this.$emit('change', {
        sortBy: this.sortKey,
        sortDir: this.sortDir,
      })
    },

    // ── Células ─────────────────────────────────────────────────────────
    fullAddress(occ) {
      if (!occ.address) return ''

      const numero = occ.number ? `, ${occ.number}` : ''
      const bairro = occ.neighborhood ? ` — ${occ.neighborhood}` : ''

      return `${occ.address}${numero}${bairro}`
    },

    isMine(occ) {
      return !!(occ.active_responsibility && occ.active_responsibility.user_id === this.$store.state.user.id)
    },

    isFromChildNode(occ) {
      const selectedNodeId = this.$store.state.selected_node?.id
      return !!occ.node_id && !!selectedNodeId && occ.node_id !== selectedNodeId
    },

    responsibleName(occ) {
      return occ.active_responsibility && occ.active_responsibility.user
        ? occ.active_responsibility.user.name
        : '—'
    },

    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('pt-BR')
    },

    protocolBadgeStyle(occ) {
      const bg = (occ.classification && occ.classification.hex_color) || '#7367f0'
      return { backgroundColor: bg, color: getContrastTextColor(bg) }
    },

    stepLabel(step, occ) {
      return occurrenceStepLabel(step, occ)
    },

    stepBadgeStyle(step, occ) {
      if (step === 'despacha') {
        return occ && occ.dispatches_count > 0
          ? { backgroundColor: '#2563eb', color: '#fff' }
          : { backgroundColor: '#f59e0b', color: '#fff' }
      }
      const colors = {
        regulacao: { backgroundColor: '#2563eb', color: '#fff' },
      }
      return colors[step] || { backgroundColor: '#6b7280', color: '#fff' }
    },
  },
}
</script>

<style scoped>
/* Tempo-resposta: selo na cor da classificação; pisca enquanto nenhuma equipe chegou ao local. */
.response-badge {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

@keyframes piscar {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

.piscar {
  animation: piscar 1.5s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .piscar { animation: none; }
}

.occ-node-hint {
  font-size: 10px;
  color: #4b4b4b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
}
.occ-row--mine td {
  background-color: rgba(115, 103, 240, 0.06);
}
.occ-row--mine td:first-child {
  box-shadow: inset 3px 0 0 #7367f0;
}
.occ-mine-label {
  font-weight: 700;
  color: #7367f0;
}
.dark-layout .occ-row--mine td {
  background-color: rgba(115, 103, 240, 0.14);
}
/* sticky-left/right têm fundo sólido (não dá pra ser transparente, senão o
   conteúdo rolado por baixo aparece) — aqui é a mesma cor já pré-misturada
   sobre o branco/escuro de base, pra manter a linha toda com um tom só. */
.occ-table tbody tr.occ-row--mine td.sticky-left,
.occ-table tbody tr.occ-row--mine td.sticky-right {
  background: #f7f6fe;
}
.occ-table tbody tr.occ-row--mine:hover td.sticky-left,
.occ-table tbody tr.occ-row--mine:hover td.sticky-right {
  background: #efedfc;
}
.dark-layout .occ-table tbody tr.occ-row--mine td.sticky-left,
.dark-layout .occ-table tbody tr.occ-row--mine td.sticky-right {
  background: #33385e;
}
.dark-layout .occ-table tbody tr.occ-row--mine:hover td.sticky-left,
.dark-layout .occ-table tbody tr.occ-row--mine:hover td.sticky-right {
  background: #3b4169;
}

.occ-table-wrapper {
  /* Sem altura máxima: a lista cresce e quem rola é a página. Só a rolagem horizontal fica,
     para telas estreitas (as colunas Protocolo e Ações continuam fixas nas laterais). */
  overflow-x: auto;
}

.occ-table {
  font-size: 0.85rem;
}
.occ-table th,
.occ-table td {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}

.occ-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f3f2f7;
  white-space: nowrap;
  border-top: none;
}
.dark-layout .occ-table thead th {
  background: #343d55;
}

.occ-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.occ-table th.sortable:hover {
  color: #7367f0;
}

.occ-table th.sticky-left,
.occ-table td.sticky-left {
  position: sticky;
  left: 0;
  background: #fff;
}
.occ-table th.sticky-right,
.occ-table td.sticky-right {
  position: sticky;
  right: 0;
  background: #fff;
}
.occ-table td.sticky-left,
.occ-table td.sticky-right {
  z-index: 2;
}
.occ-table thead th.sticky-left,
.occ-table thead th.sticky-right {
  z-index: 4;
  background: #f3f2f7;
}
.occ-table td.sticky-left {
  box-shadow: 1px 0 0 #ebe9f1;
}
.occ-table td.sticky-right {
  box-shadow: -1px 0 0 #ebe9f1;
}

.occ-table tbody tr:hover td.sticky-left,
.occ-table tbody tr:hover td.sticky-right {
  background: #f6f6f9;
}
.dark-layout .occ-table td.sticky-left,
.dark-layout .occ-table td.sticky-right {
  background: #283046;
}
.dark-layout .occ-table thead th.sticky-left,
.dark-layout .occ-table thead th.sticky-right {
  background: #343d55;
}
.dark-layout .occ-table tbody tr:hover td.sticky-left,
.dark-layout .occ-table tbody tr:hover td.sticky-right {
  background: #242b3d;
}
.dark-layout .occ-table td.sticky-left {
  box-shadow: 1px 0 0 #3b4253;
}
.dark-layout .occ-table td.sticky-right {
  box-shadow: -1px 0 0 #3b4253;
}

.protocol-link {
  cursor: pointer;
}
.protocol-link:hover {
  filter: brightness(1.12);
  text-decoration: underline;
}
.protocol-link:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 1px;
}

.occ-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.cell-truncate {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
